import { formatKioskTime, kioskTimeZone } from '../time/kiosk-time';
import { QrScanErrorType, VisitFlowInfo } from './visit.types';

export interface QrErrorCopy {
  title: string;
  subtitle: string;
}

/**
 * SPECS §4.3.1 — copy for each stage of the scan flow that can fail. Shared by
 * both `/qr-error` (1st attempt) and `/qr-error-manual` (2nd attempt) so the
 * second screen shows the SAME error message as the first, per SPECS §4.2.
 */
const STAGE_COPY: Partial<Record<QrScanErrorType, QrErrorCopy>> = {
  no_reader:       { title: 'Le lecteur ne répond pas', subtitle: 'Veuillez réessayer de nouveau' },
  empty_scan:      { title: 'QR code non lu',          subtitle: 'Veuillez réessayer de nouveau' },
  api_unreachable: { title: 'Erreur de connexion',     subtitle: 'Veuillez réessayer de nouveau' },
  not_recognized:  { title: 'QR code non reconnu',     subtitle: 'Vérifiez votre invitation email et réessayez de nouveau' },
};

const DEFAULT_STAGE_COPY: QrErrorCopy = STAGE_COPY.not_recognized!;

/**
 * Resolve the error copy to display for a failed scan: "Arrivée déjà signalée"
 * for a rescanned pass whose arrival is still open, "Accès non autorisé" for a
 * recognized-but-denied pass (early/late/canceled), otherwise the copy of the
 * stage that failed. Used identically by both QR error screens.
 */
export function qrErrorCopy(flow: VisitFlowInfo | undefined): QrErrorCopy {
  const type = flow?.qrScanErrorType;
  return (
    alreadyCheckedInCopy(flow) ??
    accessDeniedCopy(flow) ??
    ((type && STAGE_COPY[type]) || DEFAULT_STAGE_COPY)
  );
}

/**
 * « Arrivée déjà signalée » : le visiteur rescanne un pass valide dont l'arrivée
 * du jour est déjà en base et dont le départ n'a pas été signalé.
 *
 * Symétrique du message de départ (`checkout-scan-qr`), et volontairement séparé
 * de `accessDeniedCopy` : l'accès n'est pas refusé, il est déjà accordé — l'hôte
 * a été notifié au premier scan. Renvoie null pour tous les autres échecs.
 */
export function alreadyCheckedInCopy(flow: VisitFlowInfo | undefined): QrErrorCopy | null {
  if (flow?.qrScanErrorType !== 'already_checked_in') {
    return null;
  }

  const already = flow.qrAlreadyCheckedIn;
  const name = `${already?.firstName ?? ''} ${already?.lastName ?? ''}`.trim();
  // L'heure peut manquer si l'API n'a pas su la relire : la phrase reste juste
  // sans elle, alors qu'un « à  » vide serait une anomalie visible à l'écran.
  const time = formatKioskTime(already?.checkinAt);
  const when = time ? ` à ${time}` : '';

  return {
    title: 'Arrivée déjà signalée',
    subtitle: name
      ? `${name}, votre arrivée a déjà été signalée aujourd'hui${when}.`
      : `Votre arrivée a déjà été signalée aujourd'hui${when}.`,
  };
}

/**
 * Literal French appointment date, e.g. "29 juin 2026 à 14:30".
 * The timezone comes from `environment.timeZone` (the platform timezone, cf.
 * TIMEZONE in the API env): the WWS `start_date` is a Unix timestamp, so a kiosk
 * whose OS clock is set to a fixed UTC+1 (no DST) would otherwise show the RDV
 * one hour early in summer.
 */
function appointmentFormat(): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: kioskTimeZone(),
  });
}

/** Format a Unix-seconds timestamp for display, or null when unavailable. */
export function formatAppointment(ts: number | null | undefined): string | null {
  if (!ts) return null;
  const date = new Date(ts * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return appointmentFormat().format(date);
}

/**
 * "Accès non autorisé" copy for a recognized pass that is outside its valid
 * window (early/late/canceled). Returns null for every other failure so each
 * screen can apply its own default copy.
 */
export function accessDeniedCopy(flow: VisitFlowInfo | undefined): QrErrorCopy | null {
  const type = flow?.qrScanErrorType;

  if (type === 'cancelled') {
    return { title: 'Accès non autorisé', subtitle: 'Votre rendez-vous a été annulé.' };
  }

  if (type === 'early' || type === 'late') {
    const lead = type === 'early' ? 'Vous êtes arrivé en avance.' : 'Vous êtes arrivé trop tard.';
    const was = type === 'early' ? 'est' : 'était';
    const when = formatAppointment(flow?.qrAppointmentDate);
    return {
      title: 'Accès non autorisé',
      subtitle: when ? `${lead} Votre rendez-vous ${was} prévu le ${when}.` : lead,
    };
  }

  return null;
}
